import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import Intercom, { shutdown as intercomShutdown } from '@intercom/messenger-js-sdk';
import config from '../../config';
import { getUserToken, getUserData } from '../../utils/permissions';

const INTERCOM_APP_ID = process.env.REACT_APP_INTERCOM_APP_ID || 'l3d0sdmx';

// Single source of truth for Intercom boot/shutdown.
//
// Reads user from localStorage (getUserToken + getUserData) — NOT from
// Redux. The reason: Login.js dispatches LOGIN_RESET right after
// LOGIN_SUCCESS, which wipes Redux back to initialState. Nothing
// re-hydrates Redux until a page refresh. The rest of the app survives
// because most components read from localStorage too. localStorage is
// written by LOGIN_SUCCESS *before* LOGIN_RESET fires, and only
// cleared by logoutApi(), so it's the only stable signal mid-session.
//
// Mounted INSIDE the Router (in AppLayout + NoSidebarLayout) so it can
// use useLocation. On every route change we re-fetch
// /my/intercom-enabled — that's what gives "on-the-fly" sync when an
// admin flips the org-level toggle.
//
// Decision tree (per effect run):
//   1. No localStorage token         → shutdown, no backend fetch
//   2. Token + userData present      → fetch flag → boot or shutdown
//
// Unmount cleanup calls shutdown so logout (navigation to /login,
// which has no layout) takes the bubble down cleanly.
const IntercomGate = () => {
  const location = useLocation();

  useEffect(() => {
    const token = getUserToken();
    const userData = getUserData();

    if (!token || !userData?.id) {
      intercomShutdown();
      return;
    }

    let cancelled = false;
    axios
      .get(`${config.api.base_url}/organizations/my/intercom-enabled`, {
        headers: { Authorization: 'Bearer ' + token },
      })
      .then((res) => {
        if (cancelled) return;
        const enabled = res.data?.data?.intercom_enabled === true;
        if (enabled) {
          Intercom({
            app_id: INTERCOM_APP_ID,
            user_id: userData.id,
            intercom_user_jwt: userData.intercom_user_jwt || undefined,
          });
        } else {
          intercomShutdown();
        }
      })
      .catch(() => {
        // Transient network failure — leave current state alone.
      });

    return () => {
      cancelled = true;
    };
  }, [location.pathname]);

  // Shutdown on unmount (covers logout, since /login has no layout and
  // therefore no IntercomGate mounted there).
  useEffect(() => {
    return () => intercomShutdown();
  }, []);

  return null;
};

export default IntercomGate;
